/* eslint-disable no-nested-ternary */
import { isProduction } from '@application/env';
import type { Language } from '@application/types';
import type { LanguageCode } from '@gql/graphql';
import { CountryCode } from '@gql/graphql';
import type { SelectOption } from '@presentation/components';
import imageCompression from 'browser-image-compression';
import HeicConverter from 'heic-convert/browser';
import dayjs from 'dayjs';
import { isArray, isObject, isPlainObject, map, mapValues, sortBy } from 'lodash/fp';
import Cookies from 'universal-cookie';
import { DOCUMENT_FILE_MAX_SIZE } from '@application/components/controllers/pages/MtBreederOnboardingPageController/useValidationSchema';

export * from './format';
export * from './hooks';
export * from './readableDate';
export * from './routing';
export * from './selectOptions';
export * from './social';
export * from './string';
export * from './encodeUri';
export * from './scrollToError';
export * from './formatWhatsAppUrl';

const generatePromoIndexes = (end: number) => {
  const result = [];
  let current = 3;

  while (current <= end) {
    result.push(current);
    // Alternate between adding 2 and 6
    current += result.length % 2 === 0 ? 6 : 2;
  }

  return result;
};

export const isPromoItem = (itemIndex: number, pageSize = 32): boolean => {
  const indexes = generatePromoIndexes(pageSize);
  return indexes.some((position) => {
    return (itemIndex % pageSize) - position === 0;
  });
};

export const generateItemsWithPromos = <TItem, TPromoItem>(
  items: TItem[],
  promoItems: TPromoItem[],
  size = 32,
) => {
  const list: (TPromoItem | TItem)[] = [];
  let promoIndex = 0;
  let itemIndex = 0;

  if (items.length === 0) return { list, itemAmount: itemIndex, promoAmount: promoIndex };

  for (let i = 0; i < size; i += 1) {
    if (isPromoItem(i, size)) {
      list.push(promoItems[promoIndex]);
      promoIndex = (promoIndex + 1) % promoItems.length;
    } else {
      list.push(items[itemIndex]);
      itemIndex += 1;
    }
    if (itemIndex >= items.length) {
      break;
    }
  }

  return { list, itemAmount: itemIndex, promoAmount: promoIndex };
};

export const generateItemsWithPromosAmounts = (size: number) =>
  generateItemsWithPromos(new Array(size).fill(1), [1], size);

export const getRandomIntInclusive = (min: number, max: number): number => {
  // eslint-disable-next-line no-param-reassign
  min = Math.ceil(min);
  // eslint-disable-next-line no-param-reassign
  max = Math.floor(max);
  // The maximum is inclusive and the minimum is inclusive
  return Math.floor(Math.random() * (max - min + 1) + min);
};

export const asyncTimeout = (callback: any, ms: number) =>
  new Promise((resolve) =>
    setTimeout(() => {
      callback();
      resolve(undefined);
    }, ms),
  );

export const sortSelectOptionByLabel = (selectOptions: SelectOption<string>[]) => {
  return sortBy((x) => x.label, selectOptions);
  // const sortedValues = selectOptions.map((option) => option.value).sort();
  // return sortedValues.map((value) =>
  //   selectOptions.find((option) => option.value === value),
  // ) as SelectOption<string>[];
};

export const getCookies = (input?: string) =>
  new Cookies(input, {
    decode: (value) => decodeURIComponent(value),
  });

export const cookies = getCookies();

export function filterNotEmpty<T>(x: T | undefined | null): x is T {
  return x !== undefined && x !== null;
}

// https://stackoverflow.com/questions/47232518/write-a-typesafe-pick-function-in-typescript
export function pick<T, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  const ret: any = {};
  keys.forEach((key) => {
    ret[key] = obj[key];
  });
  return ret;
}

export function translatedFieldWithFallback<
  TTranslations extends Array<Record<string, any> & { language: string }>,
>(
  currentLanguage: Language,
  translations: TTranslations | undefined | null,
  field: keyof TTranslations[0],
) {
  if (!translations?.length) return;

  const valueInCurrentLanguage = translations?.find((x) => x.language === currentLanguage)?.[
    field as any
  ];
  if (valueInCurrentLanguage) return valueInCurrentLanguage as string;
  const valueFallback = translations?.filter(
    (x) => Object.hasOwn(x, field) && !!x[field as keyof x],
  )?.[0]?.[field as any];
  if (valueFallback) return valueFallback as string;
}

export function isFieldsAvailableInLanguage<
  TTranslations extends Array<Record<string, any> & { language: string }>,
>(
  currentLanguage: Language,
  translations: TTranslations | undefined | null,
  fields: Array<keyof TTranslations[0]>,
) {
  if (!translations?.length) return;

  const translationInCurrentLanguage = translations?.find((x) => x.language === currentLanguage);
  return (
    translationInCurrentLanguage &&
    fields.every((field) => {
      return (
        Object.hasOwn(translationInCurrentLanguage, field) && !!translationInCurrentLanguage[field]
      );
    })
  );
}
export const languageCodeToLanguage = (languageCode: LanguageCode): Language => {
  const code = languageCode.split('_')[0].toLowerCase();
  if (['fr', 'nl', 'de'].includes(code)) {
    return code as Language;
  }
  return 'fr';
};

export const isImage = (name: string) => /\.(jpe?g|png|heif|heic|webp|gif)$/i.test(name);
export const isBlob = (name: string) => name.includes('blob:');
export const isPdf = (name: string) => /\.(pdf)$/i.test(name);
export const isWord = (name: string) => /\.(doc?x)$/i.test(name);

// https://github.com/Kikobeats/map-values-deep
export const mapValuesDeep = (obj, fn, key?) =>
  isArray(obj)
    ? map((innerObj, idx) => mapValuesDeep(innerObj, fn, idx))(obj)
    : isPlainObject(obj)
    ? mapValues((val, key) => mapValuesDeep(val, fn, key))(obj)
    : isObject(obj)
    ? obj
    : fn(obj, key);

export const formatDateByCountry = (date: string, countryCode: CountryCode) => {
  if (countryCode === CountryCode.DE) {
    return dayjs(date).format('D[.] MMMM YYYY');
  }
  return dayjs(date).format('D MMMM YYYY');
};

export const clearCookieFromAllPathsAndDomains = (cookieKey: string) => {
  const paths = [
    '/',
    '/be',
    '/nl',
    '/fr',
    '/ch',
    '/de',
    '/country-selector',
    '/be/fr',
    '/be/nl',
    '/fr/fr',
    '/de/de',
    '/nl/nl',
    '/ch/fr',
    '/ch/de',
  ];
  const domains = isProduction() ? ['tipaw.com'] : ['tipaw.com', '.staging.tipaw.com', 'localhost'];
  domains.forEach((domain) => {
    paths.forEach((path) => {
      cookies.remove(cookieKey, { path, domain });
    });
  });
};

export const convertHeicHeifToPng = async (fileToConvert: File) => {
  const arrayBuffer = await fileToConvert.arrayBuffer();
  const buffer = new Uint8Array(arrayBuffer);
  const outputBuffer = await HeicConverter({
    buffer,
    format: 'JPEG',
  });

  const fileName = fileToConvert.name
    .toLowerCase()
    .replaceAll('.heic', '.jpeg')
    .replaceAll('.heif', '.jpeg');

  const convertedFile = new File([new Blob([outputBuffer])], fileName, { type: 'image/jpeg' });
  const compressedFile = await compressImage(convertedFile);
  return compressedFile;
};

export const convertHeicToBlob = async (fileToConvert: File) => {
  const arrayBuffer = await fileToConvert.arrayBuffer();
  const buffer = new Uint8Array(arrayBuffer);
  const outputBuffer = await HeicConverter({
    buffer,
    format: 'JPEG',
  });
  return new Blob([outputBuffer]);
};

export const compressImage = async (file: File, maxCompressedSizeMb = 8) => {
  try {
    const fileName = file.name;
    const compressedBlob = await imageCompression(file, {
      maxSizeMB: maxCompressedSizeMb,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    });

    if (!compressedBlob) throw new Error('file/not-found');

    const compressedFile = new File([compressedBlob], fileName, {
      lastModified: compressedBlob.lastModified,
      type: compressedBlob.type,
    });

    return compressedFile;
  } catch (err) {
    console.log(err);
    return file;
  }
};
